export type ScheduledJobStatus =
  | 'active'
  | 'paused'
  | 'completed'
  | 'expired'
  | 'failed';

/** Jobs a user can still act on. */
export const LIVE_STATUSES: ScheduledJobStatus[] = ['active', 'paused'];

/** Mirrors the backend default `scheduled_jobs_max_prompt_chars`; a lower tenant cap still answers 400. */
export const SCHEDULED_JOB_PROMPT_MAX_CHARS = 8000;

export type ScheduledJobsSort =
  | 'nextRunAt'
  | 'createdDate'
  | 'lastRunAt'
  | 'name'
  | 'status';

export type ScheduledJobsOrder = 'asc' | 'desc';

/** Mirrors backend `ScheduleSpecDto` (scheduled_job_routes.py). */
export type ScheduleSpecDto = {
  scheduleKind: 'cron' | 'once';
  cron?: string | null;
  runAt?: string | null;
  timezone: string;
  startAt?: string | null;
  endAt?: string | null;
  maxRuns?: number | null;
};

export type ScheduledJobItem = {
  id: string;
  name: string;
  description?: string | null;
  /** Job prompt text (partial PATCH field). Falls back to description when absent. */
  prompt?: string | null;
  status: ScheduledJobStatus | string;
  agentId?: string | null;
  /** Display name of the job's agent. Empty when the agent no longer exists. */
  agentName?: string | null;
  conversationId?: string | null;
  nextRunAt?: string | null;
  /** Timezone-aware next run from the API, e.g. 2026-08-17T11:15:00+06:00 */
  nextRunAtLocal?: string | null;
  lastRunAt?: string | null;
  createdDate?: string | null;
  /** Require approval for actions (HIL). Absent on older BE responses = false. */
  hilEnabled?: boolean;
  /** Email the owner when a run finishes. Absent (older BE / legacy job) = on. */
  getMail?: boolean;
  /** In-app notification when a run finishes. Absent = on. */
  pushNotification?: boolean;
  /** Full schedule spec, needed to seed the edit form. Absent on older BE responses. */
  schedule?: ScheduleSpecDto | null;
  /** A run is currently in flight — gates Pause/Run-now affordances. */
  running?: boolean;
};

/** Partial PATCH body for `PATCH /scheduled-jobs/{id}` — send only changed fields. */
export type UpdateScheduledJobPayload = {
  name?: string;
  description?: string;
  prompt?: string;
  hilEnabled?: boolean;
  getMail?: boolean;
  pushNotification?: boolean;
  schedule?: ScheduleSpecDto;
};

export type GetScheduledJobsPayload = {
  agentId?: string;
  /** One status, or several to list them together (sent as `status=a,b`). */
  status?: ScheduledJobStatus | ScheduledJobStatus[];
  conversationId?: string;
  q?: string;
  skip?: number;
  take?: number;
  sort?: ScheduledJobsSort;
  order?: ScheduledJobsOrder;
};

export type GetScheduledJobsResponse = {
  items: ScheduledJobItem[];
  total: number;
  skip: number;
  take: number;
};

/** Mirrors backend `NextOccurrencesResponse` — `POST /scheduled-jobs/preview`. */
export type SchedulePreviewResponse = {
  occurrences: string[];
  occurrencesLocal: string[];
  timezone: string;
  humanReadable: string;
  nowUtc: string;
};

export type ScheduledJobRunStatus = string;

/** Subset of backend `ScheduledJobRunItem` shown in the edit modal's recent-runs list. */
export type ScheduledJobRunItem = {
  id: string;
  status: ScheduledJobRunStatus;
  trigger: string;
  scheduledFor?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  errorMessage?: string | null;
};

export type ScheduledJobRunListResponse = {
  items: ScheduledJobRunItem[];
  total: number;
  skip: number;
  take: number;
};

export type ScheduledJobStatusResponse = {
  status: string;
  nextRunAt?: string | null;
};
